import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";

// API base MUST be /api/<appName> (OBTO public-app baseline §7).
const API_BASE = "/api/jev-judge";
const SUBMIT_VIEW = "__submit__";

// Three real items from our own queue, pinned so a first-time visitor sees a
// strong report, a report that slipped scope, and an agent that said nothing.
const FEATURED: { key: string; caption: string }[] = [
  { key: "WT-14", caption: "Strongest report in the queue; one claim still lacks a receipt" },
  { key: "WT-15", caption: "Plans a write the requester did not ask for" },
  { key: "WT-12", caption: "Marked done with no report at all" }
];

type NoulA = { type: "noul"; noul: number };
type ChoiceA = { type: "choice"; choice: string; confidence: number; probabilities: Record<string, number> };
type ScoreA = { type: "score"; score: number; confidence: number; legend: Record<string, string>; probabilities: Record<string, number> };
type Answer = NoulA | ChoiceA | ScoreA;

type Redaction = { applied: boolean; note: string; masked_terms: number; replacements?: number; categories?: Record<string, number> };
type Grade = {
  ok: boolean; key: string; title: string; status: string; model: string; request_id?: string | null;
  answers: Record<string, Answer>; verdicts: Record<string, string>;
  usage: { input_tokens: number; output_tokens: number }; latency_ms: number; cost_usd: number;
  state_chars: number; request?: { model: string; state: unknown; questions: unknown };
  redaction?: Redaction; graded_at: string; source?: string;
  cached?: boolean; first_graded_at?: string; cache_age_seconds?: number; rate_remaining?: number;
  error?: string; message?: string;
};
type Item = { key: string; title: string; status: string; appName: string | null; clusterLabel: string | null; hasEnrichment: boolean; enrichedAt: string | null; cached?: boolean };
type Rubric = { version: string; model: string; questions: Record<string, { type: string; instructions: string; criteria: unknown }>; thresholds: { noul_pass: number; noul_fail: number; choice_confidence_act: number } };

async function getJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  const ctype = res.headers.get("content-type") || "";
  if (!ctype.includes("application/json")) throw new Error("Non-JSON response (" + res.status + ") from " + url);
  const body = (await res.json()) as T & { ok?: boolean; error?: string; message?: string };
  if (body && body.ok === false) throw new Error(body.message || body.error || "Request failed");
  return body as T;
}
const pct = (x: number) => Math.round(x * 100) + "%";
const money = (x: number) => (x === 0 ? "$0" : "$" + x.toFixed(x < 0.001 ? 6 : 4));
const words = (s: string) => String(s || "").replace(/_/g, " ");
const sentence = (s: string) => { const w = words(s); return w.charAt(0).toUpperCase() + w.slice(1); };
const ago = (secs: number) => (secs < 90 ? "just now" : secs < 5400 ? Math.round(secs / 60) + " min ago" : secs < 172800 ? Math.round(secs / 3600) + " h ago" : Math.round(secs / 86400) + " days ago");

// Plain-language names for the rubric keys. The keys stay as they are on the
// wire (the model never sees them); this is only what a visitor reads.
const LABEL: Record<string, string> = {
  target_verified_by_probe: "Target was actually looked up",
  cause_grounded_in_evidence: "Every claim has a receipt",
  acceptance_testable: "Someone could verify the result",
  evidence_refs_are_concrete: "Evidence names real sources",
  cause_addresses_stated_problem: "Answers the question that was asked",
  code_change_restriction: "Stayed inside the requested scope",
  enrichment_quality: "Report quality",
  failure_class: "Biggest defect", claims_have_receipts: "Every claim has a receipt", evidence_is_reproducible: "Evidence names things a reviewer can open or re-run", verification_is_specific: "Says how anyone could verify it", claims_answer_the_task: "Answers the task that was asked", stayed_in_scope: "Stayed inside the task", report_quality: "Report quality", biggest_gap: "Biggest gap"
};
const label = (k: string) => LABEL[k] || sentence(k);

function Copy({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button className="copy" onClick={() => {
      try { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1400); } catch (e) { /* clipboard unavailable */ }
    }}>{done ? "copied" : "copy"}</button>
  );
}

function CodeBlock({ value }: { value: unknown }) {
  const text = JSON.stringify(value, null, 2);
  return <div className="code-wrap"><Copy text={text} /><pre className="code">{text}</pre></div>;
}

function Shield({ r }: { r?: Redaction }) {
  if (!r || !r.applied) return null;
  const cats = r.categories || {};
  const names = Object.keys(cats);
  const detail = names.length ? names.map((k) => cats[k] + " " + k + (cats[k] === 1 ? "" : "s")).join(", ") : r.masked_terms + " terms";
  return <span className="shield" title={r.note}>redacted for display<span className="sep">·</span>{detail}</span>;
}

function Bar({ value, tone }: { value: number; tone: string }) {
  return <div className={"bar bar-" + tone} aria-label={pct(value)}><div className="bar-fill" style={{ width: pct(value) }} /></div>;
}

function Question({ rubric, name, open }: { rubric: Rubric | null; name: string; open: boolean }) {
  const q = rubric?.questions[name];
  if (!q || !open) return null;
  return <div className="q"><p className="q-lead">Sent to the model as <code>{name}</code></p><p>{q.instructions}</p>
    {q.criteria ? <pre className="crit">{typeof q.criteria === "string" ? q.criteria : JSON.stringify(q.criteria, null, 2)}</pre> : null}</div>;
}

function NoulRow({ name, a, verdict, rubric, open }: { name: string; a: NoulA; verdict: string; rubric: Rubric | null; open: boolean }) {
  return (
    <div className="row">
      <div className="row-head">
        <span className={"pill pill-" + verdict}>{verdict}</span>
        <span className="qname">{label(name)}</span>
        <span className="prob"><b>{pct(a.noul)}</b> yes</span>
      </div>
      <Bar value={a.noul} tone={verdict} />
      <Question rubric={rubric} name={name} open={open} />
    </div>
  );
}

function ChoiceRow({ name, a, verdict, rubric, open }: { name: string; a: ChoiceA; verdict: string; rubric: Rubric | null; open: boolean }) {
  const opts = Object.entries(a.probabilities).sort((x, y) => y[1] - x[1]);
  const uncertain = verdict.startsWith("uncertain");
  return (
    <div className="row">
      <div className="row-head">
        <span className={"pill " + (uncertain ? "pill-review" : "pill-neutral")}>{uncertain ? "uncertain" : "choice"}</span>
        <span className="qname">{label(name)}</span>
        <span className="prob"><b>{words(a.choice)}</b><span className="sep">·</span>confidence {pct(a.confidence)}</span>
      </div>
      <div className="dist">
        {opts.map(([k, p]) => (
          <div className="dist-row" key={k}>
            <span className={"opt" + (k === a.choice ? " opt-top" : "")}>{sentence(k)}</span>
            <Bar value={p} tone={k === a.choice ? "neutral" : "faint"} />
            <span className="dist-p">{pct(p)}</span>
          </div>
        ))}
      </div>
      <Question rubric={rubric} name={name} open={open} />
    </div>
  );
}

function ScoreRow({ name, a, rubric, open }: { name: string; a: ScoreA; rubric: Rubric | null; open: boolean }) {
  const levels = Object.keys(a.legend).sort((x, y) => Number(x) - Number(y));
  const top = levels.reduce((m, l) => ((a.probabilities[l] || 0) > (a.probabilities[m] || 0) ? l : m), levels[0]);
  return (
    <div className="row">
      <div className="row-head">
        <span className="pill pill-neutral">score</span>
        <span className="qname">{label(name)}</span>
        <span className="prob"><b>{a.score.toFixed(2)}</b> of {levels.length - 1}<span className="sep">·</span>confidence {pct(a.confidence)}</span>
      </div>
      <div className="dist">
        {levels.map((l) => (
          <div className="dist-row" key={l}>
            <span className={"opt" + (l === top ? " opt-top" : "")}>Level {l}</span>
            <Bar value={a.probabilities[l] || 0} tone={l === top ? "neutral" : "faint"} />
            <span className="dist-p">{pct(a.probabilities[l] || 0)}</span>
          </div>
        ))}
      </div>
      {open && (<div className="q"><p className="q-lead">What each level means</p>
        <ol start={0}>{levels.map((l) => <li key={l}>{a.legend[l]}</li>)}</ol></div>)}
    </div>
  );
}

// The probabilities are the evidence; this is the decision taken from them.
// Code owns the policy, so a threshold can move without asking the model again.
function decide(g: Grade, rubric: Rubric | null) {
  const q = (g.answers.enrichment_quality || g.answers.report_quality) as ScoreA | undefined;
  const fc = (g.answers.failure_class || g.answers.biggest_gap) as ChoiceA | undefined;
  const nouls = Object.entries(g.answers).filter(([, a]) => a.type === "noul") as [string, NoulA][];
  const failed = nouls.filter(([k]) => g.verdicts[k] === "fail").map(([k]) => k);
  const reviewed = nouls.filter(([k]) => g.verdicts[k] === "review").map(([k]) => k);
  const actAt = rubric ? rubric.thresholds.choice_confidence_act : 0.6;

  if (failed.length || (q && q.score < 2)) {
    const why = failed.length
      ? failed.length + (failed.length === 1 ? " check failed: " : " checks failed: ") + failed.map((k) => label(k).toLowerCase()).join(", ")
      : "report quality scored below level 2";
    return { label: "Did not show its work", tone: "bad", why: why };
  }
  if (reviewed.length || (fc && fc.confidence < actAt)) {
    const why = reviewed.length
      ? reviewed.length + (reviewed.length === 1 ? " check sits" : " checks sit") + " in the review band: " + reviewed.map((k) => label(k).toLowerCase()).join(", ")
      : "the biggest defect was called below the " + actAt + " confidence bar";
    return { label: "Read it yourself", tone: "warn", why: why };
  }
  return { label: "Showed its work", tone: "good", why: "every check passed and the defect call was confident" };
}

function Outcome({ g, rubric }: { g: Grade; rubric: Rubric | null }) {
  const d = decide(g, rubric);
  return (
    <div className={"outcome outcome-" + d.tone}>
      <span className="o-label">{d.label}</span>
      <span className="o-why">{d.why}</span>
    </div>
  );
}

function Summary({ g }: { g: Grade }) {
  const q = (g.answers.enrichment_quality || g.answers.report_quality) as ScoreA | undefined;
  const fc = (g.answers.failure_class || g.answers.biggest_gap) as ChoiceA | undefined;
  const nouls = Object.entries(g.answers).filter(([, a]) => a.type === "noul") as [string, NoulA][];
  const passes = nouls.filter(([k]) => g.verdicts[k] === "pass").length;
  const fails = nouls.filter(([k]) => g.verdicts[k] === "fail").length;
  return (
    <div className="summary">
      <div className="tile"><div className="t-n">{q ? q.score.toFixed(2) : "–"}<span className="t-d">of {q ? Object.keys(q.legend).length - 1 : 4}</span></div><div className="t-l">report quality</div></div>
      <div className="tile"><div className="t-n small">{fc ? sentence(fc.choice) : "–"}</div><div className="t-l">biggest defect{fc ? " · " + pct(fc.confidence) + " confidence" : ""}</div></div>
      <div className="tile"><div className="t-n"><span className={passes ? "ok" : ""}>{passes}</span><span className="t-d">of {nouls.length} pass</span></div><div className="t-l">yes/no checks{fails ? " · " + fails + " fail" : ""}</div></div>
      <div className="tile"><div className="t-n">{g.latency_ms}<span className="t-d">ms</span></div><div className="t-l">{g.usage.input_tokens.toLocaleString()} tokens · {money(g.cost_usd)}</div></div>
    </div>
  );
}

function GradePanel({ g, rubric }: { g: Grade; rubric: Rubric | null }) {
  const [tab, setTab] = useState<"answers" | "request" | "response">("answers");
  const [showQ, setShowQ] = useState(false);
  const qsIn = g.request && g.request.questions ? (g.request.questions as Rubric["questions"]) : null; const rubricQ: Rubric | null = qsIn ? { version: rubric?.version || "", model: rubric?.model || g.model, thresholds: rubric?.thresholds || { noul_pass: 0.7, noul_fail: 0.3, choice_confidence_act: 0.6 }, questions: qsIn } : rubric; const order = rubricQ ? Object.keys(rubricQ.questions) : Object.keys(g.answers);
  return (
    <section className="panel">
      <header className="panel-head">
        <div className="panel-title">
          <div className="panel-tags">
            <span className="key">{g.key}</span>
            {g.cached
              ? <span className="cachechip" title={"First graded " + new Date(g.first_graded_at || g.graded_at).toLocaleString()}>from cache<span className="sep">·</span>{g.cache_age_seconds != null ? ago(g.cache_age_seconds) : "stored"}</span>
              : <span className="livechip">live model call</span>}
            <Shield r={g.redaction} />
          </div>
          <h2>{g.title}</h2>
          <p className="meta">queue status <b>{words(g.status)}</b><span className="sep">·</span>model <code>{g.model}</code><span className="sep">·</span>state {g.state_chars.toLocaleString()} chars
            {g.request_id ? <><span className="sep">·</span>request <code>{g.request_id}</code></> : null}</p>
        </div>
        <nav className="tabs" aria-label="views">
          <button className={tab === "answers" ? "on" : ""} onClick={() => setTab("answers")}>Answers</button>
          <button className={tab === "request" ? "on" : ""} onClick={() => setTab("request")}>Request</button>
          <button className={tab === "response" ? "on" : ""} onClick={() => setTab("response")}>Response</button>
        </nav>
      </header>
      {tab === "answers" && (<>
        <Outcome g={g} rubric={rubric} />
        <Summary g={g} />
        <div className="rows-head">
          <span>{order.length} questions, one request, evaluated in parallel</span>
          <button className="ghost small" onClick={() => setShowQ(!showQ)}>{showQ ? "Hide questions" : "Show the questions"}</button>
        </div>
        <div className="rows">
          {order.map((name) => {
            const a = g.answers[name];
            if (!a) return null;
            const v = g.verdicts[name] || "";
            if (a.type === "noul") return <NoulRow key={name} name={name} a={a} verdict={v} rubric={rubricQ} open={showQ} />;
            if (a.type === "choice") return <ChoiceRow key={name} name={name} a={a} verdict={v} rubric={rubricQ} open={showQ} />;
            return <ScoreRow key={name} name={name} a={a} rubric={rubricQ} open={showQ} />;
          })}
        </div>
        <p className="limit"><b>Known limit.</b> This checks that the work was <em>shown</em>, not that it is correct. Evidence that is fabricated but convincing passes. Next step on OBTO: grade the report against the tool-call log the platform already keeps, so "shown" becomes "matches what actually happened".</p>
      </>)}
      {tab === "request" && (<>
        <p className="note">{g.redaction?.note || "The model received the full record; this display is masked for sharing."} Sent as one <code>POST https://api.typesafe.ai/v1/systemone</code> with <code>Authorization: Bearer &lt;key&gt;</code>.</p>
        <CodeBlock value={g.request} />
      </>)}
      {tab === "response" && (<>
        <p className="note">Exactly what came back: a probability per option, never text.</p>
        <CodeBlock value={{ model: g.model, request_id: g.request_id, answers: g.answers, usage: g.usage }} />
      </>)}
    </section>
  );
}

// Two ready-made agent reports. Same task, two ways of reporting it.
const EXAMPLES = {
  vague: {
    title: "Fix: Pay button does nothing on iPhone",
    problem: "On iPhone the Pay button on the checkout page does not respond to taps. Desktop works. Started after last Tuesday's release.",
    expected: "Tapping Pay opens the payment sheet.",
    where: "checkout page, iOS Safari 17",
    suspectedCause: "Done. The tap handler was not firing on iOS because of an overlay. I fixed the handler and removed the overlay. Tests pass and checkout works on mobile now.",
    acceptance: "Checkout works on mobile.",
    evidence: "Ran the test suite, everything green."
  },
  receipts: {
    title: "Fix: Pay button does nothing on iPhone",
    problem: "On iPhone the Pay button on the checkout page does not respond to taps. Desktop works. Started after last Tuesday's release.",
    expected: "Tapping Pay opens the payment sheet.",
    where: "checkout page, iOS Safari 17",
    suspectedCause: "PayButton.tsx binds onClick only; iOS Safari fires touchend on the sibling div.overlay (z-index 20, introduced in commit 4f1c2a9 on Tuesday) and the click never reaches the button. Added onTouchEnd and set pointer-events:none on the overlay while the sheet is closed.",
    acceptance: "On an iPhone with iOS Safari 17, open /checkout, tap Pay: the payment sheet opens within 1 s. Cypress test checkout_mobile_pay.cy.ts asserts the sheet is visible.",
    evidence: "git log -1 4f1c2a9 (Tuesday): adds div.overlay with z-index:20 in src/checkout/Overlay.css line 12. git diff src/checkout/PayButton.tsx lines 41-58: onTouchEnd handler added next to onClick; Overlay.css line 12 now sets pointer-events:none while the sheet is closed. cypress run: 1 passing (checkout_mobile_pay.cy.ts). BrowserStack session iPhone 15 / Safari 17.4: payment sheet opens on tap."
  }
};

function SubmitPanel({ onGraded, busy, setBusy }: { onGraded: (g: Grade) => void; busy: boolean; setBusy: (b: boolean) => void }) {
  const [f, setF] = useState({ ...EXAMPLES.vague });
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });

  const send = async () => {
    setErr(null); setBusy(true);
    try {
      const submission = {
        title: f.title, problem: f.problem, expected: f.expected, where: f.where,
        suspectedCause: f.suspectedCause, acceptance: f.acceptance,
        evidence: f.evidence ? [{ kind: "note", ref: null, detail: f.evidence }] : []
      };
      const g = await getJson<Grade>(API_BASE + "/raw", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ submission })
      });
      onGraded(g);
    } catch (e: unknown) { setErr(String((e as Error).message || e)); }
    finally { setBusy(false); }
  };

  return (
    <section className="panel">
      <header className="panel-head">
        <div className="panel-title">
          <div className="panel-tags"><span className="key">YOUR AGENT'S REPORT</span><span className="livechip">one live call, ~300 ms</span></div>
          <h2>Paste what your agent told you. See whether it showed its work.</h2>
          <p className="meta">Eight fixed questions, one request, probabilities back. An identical report is answered from the cache rather than charged again.</p>
        </div>
      </header>
      <div className="form">
        <div className="f-examples">
          <span>Try an example:</span>
          <button className="ghost small" disabled={busy} onClick={() => setF({ ...EXAMPLES.vague })}>"Done, tests pass"</button>
          <button className="ghost small" disabled={busy} onClick={() => setF({ ...EXAMPLES.receipts })}>Same fix, with receipts</button>
        </div>
        <label className="f-row"><span>Task title</span><input value={f.title} onChange={set("title")} placeholder="One line naming what the agent was asked to do" maxLength={300} /></label>
        <label className="f-row"><span>What was asked</span><textarea value={f.problem} onChange={set("problem")} rows={3} placeholder="The problem or task, as it was given to the agent" maxLength={1600} /></label>
        <label className="f-row"><span>What the agent claims it found or did</span><textarea value={f.suspectedCause} onChange={set("suspectedCause")} rows={3} placeholder="The agent's own summary, pasted as written" maxLength={1600} /></label>
        <label className="f-row"><span>Evidence it cites</span><textarea value={f.evidence} onChange={set("evidence")} rows={2} placeholder="Diff paths, log lines, test output, record ids, URLs" maxLength={700} /></label>
        <label className="f-row"><span>How anyone could verify it</span><input value={f.acceptance} onChange={set("acceptance")} placeholder="The check a reviewer would run to prove it" maxLength={900} /></label>
        <div className="f-grid">
          <label className="f-row"><span>Expected outcome <i>optional</i></span><input value={f.expected} onChange={set("expected")} placeholder="What should happen when it is right" maxLength={900} /></label>
          <label className="f-row"><span>Where <i>optional</i></span><input value={f.where} onChange={set("where")} placeholder="Screen, route or module" maxLength={300} /></label>
        </div>
        {err && <p className="error">{err}</p>}
        <div className="f-foot">
          <button disabled={busy || f.title.trim().length < 8 || f.problem.trim().length < 20} onClick={send}>{busy ? "Grading…" : "Did it show its work?"}</button>
        </div>
        <p className="f-note">Live grades are capped per visitor per day, so the demo cannot be run up as a bill. Nothing you type is stored against your name. What you paste is sent to TypeSafe AI to be graded, so keep secrets out of it.</p>
      </div>
    </section>
  );
}

function App() {
  const [items, setItems] = useState<Item[]>([]);
  const [rubric, setRubric] = useState<Rubric | null>(null);
  const [grades, setGrades] = useState<Record<string, Grade>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<string | null>(SUBMIT_VIEW);
  const [err, setErr] = useState<string | null>(null);
  const [gradingAll, setGradingAll] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    getJson<{ ok: boolean; items: Item[]; rubric: Rubric }>(API_BASE + "/items?limit=40")
      .then((r) => { setItems(r.items); setRubric(r.rubric); })
      .catch((e) => setErr(String(e.message || e)));
  }, []);

  const grade = async (key: string, select = true) => {
    setBusy((b) => ({ ...b, [key]: true })); setErr(null); if (select) setSelected(key);
    try {
      const g = await getJson<Grade>(API_BASE + "/sdlc/" + encodeURIComponent(key));
      setGrades((s) => ({ ...s, [key]: g }));
    } catch (e: unknown) { setErr(String((e as Error).message || e)); }
    finally { setBusy((b) => ({ ...b, [key]: false })); }
  };

  const gradeAll = async () => {
    setGradingAll(true); setErr(null);
    const todo = items.filter((it) => !grades[it.key]).map((it) => it.key);
    for (let i = 0; i < todo.length; i += 2) {
      const pair = todo.slice(i, i + 2);
      setSelected(pair[0]);
      await Promise.all(pair.map((k) => grade(k, false)));
    }
    setGradingAll(false);
  };

  // Only LIVE calls count toward tokens, latency and spend. A cached grade
  // carries the latency and cost of the day it was first computed, and folding
  // those historical numbers into "this visit" would overstate both.
  const totals = useMemo(() => {
    const gs = Object.values(grades);
    const live = gs.filter((g) => !g.cached);
    return {
      n: gs.length,
      cached: gs.filter((g) => g.cached).length,
      live: live.length,
      liveTokens: live.reduce((a, g) => a + g.usage.input_tokens, 0),
      liveMs: live.reduce((a, g) => a + g.latency_ms, 0),
      usd: live.reduce((a, g) => a + g.cost_usd, 0)
    };
  }, [grades]);

  const byKey = useMemo(() => Object.fromEntries(items.map((it) => [it.key, it])), [items]);
  const featured = FEATURED.map((f) => ({ ...f, item: byKey[f.key] })).filter((f) => f.item);
  const featuredKeys = new Set(featured.map((f) => f.key));
  const rest = items.filter((it) => !featuredKeys.has(it.key));

  const sel = selected && selected !== SUBMIT_VIEW ? grades[selected] : null;
  const selBusy = selected && selected !== SUBMIT_VIEW ? !!busy[selected] : false;

  const renderItem = (it: Item, caption?: string) => {
    const g = grades[it.key];
    const q = g?.answers?.enrichment_quality as ScoreA | undefined;
    const fc = g?.verdicts?.failure_class || "";
    const isBusy = !!busy[it.key];
    return (
      <li key={it.key} className={"item" + (selected === it.key ? " sel" : "") + (isBusy ? " busy" : "")} onClick={() => (g ? setSelected(it.key) : grade(it.key))}>
        <div className="item-line">
          <span className="key">{it.key}</span>
          <span className={"tag tag-" + it.status}>{words(it.status)}</span>
          <span className={"enr" + (it.hasEnrichment ? " yes" : "")}>{it.hasEnrichment ? "has a report" : "no report"}</span>
        </div>
        <div className="item-title">{it.title}</div>
        {caption ? <div className="item-caption">{caption}</div> : null}
        <div className="item-foot">
          {g && q ? (
            <span className="result">
              <b>{q.score.toFixed(1)}</b><span className="dim">/4</span>
              <span className={"fc" + (fc.startsWith("uncertain") ? " fc-uncertain" : fc === "none" ? " fc-none" : " fc-bad")}>{words(fc.replace("uncertain:", ""))}{fc.startsWith("uncertain") ? " ?" : ""}</span>
            </span>
          ) : isBusy ? (
            <span className="grading"><span className="spin" />grading…</span>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); grade(it.key); }}>{it.cached ? "Open" : "Grade"}</button>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="app">
      <header className="top">
        <div className="brand"><span className="dot" />Jev Judge<span className="by">on OBTO</span></div>
        <h1>Your agent says it's done. Did it show its work?</h1>
        <p className="lede">
          Paste the report your coding agent wrote. A decision model, <b>TypeSafe AI's Jev</b>, not an LLM, checks whether each claim is backed by cited evidence, the verification step is real, and the scope was respected.
          Probabilities back in a few hundred milliseconds for about a hundredth of a cent. No generated text, nothing to parse.
        </p>
        <div className="stats">
          <div className="stat"><div className="s-n">{totals.n}</div><div className="s-l">reports graded{totals.cached ? " · " + totals.cached + " from cache" : ""}</div></div>
          <div className="stat"><div className="s-n">{totals.liveTokens.toLocaleString()}</div><div className="s-l">tokens billed this visit</div></div>
          <div className="stat"><div className="s-n">{totals.live ? Math.round(totals.liveMs / totals.live) : 0}<span className="s-u">ms</span></div><div className="s-l">avg latency, {totals.live} live {totals.live === 1 ? "call" : "calls"}</div></div>
          <div className="stat"><div className="s-n">{money(totals.usd)}</div><div className="s-l">spent this visit</div></div>
        </div>
        {err && <p className="error">{err}</p>}
      </header>

      <div className="layout">
        <aside className="list">
          <button className={"submit-cta" + (selected === SUBMIT_VIEW ? " on" : "")} onClick={() => setSelected(SUBMIT_VIEW)}>
            <span className="plus">+</span>Grade your own agent's report
          </button>
          <div className="list-head">
            <h3>Start here <span className="muted">three real reports from our queue</span></h3>
          </div>
          <ul className="items featured">{featured.map((f) => renderItem(f.item, f.caption))}</ul>
          <div className="list-head">
            <h3>Everything else in the queue <span className="muted">{rest.length}</span></h3>
            <div className="list-actions">
              <button className="ghost small" onClick={() => setShowAll(!showAll)}>{showAll ? "Hide" : "Show"}</button>
              {showAll ? <button className="ghost small" disabled={gradingAll || !items.length} onClick={gradeAll}>{gradingAll ? "Grading…" : "Grade all"}</button> : null}
            </div>
          </div>
          {showAll ? <ul className="items">{rest.map((it) => renderItem(it))}</ul> : null}
          {rubric && (
            <details className="rubric"><summary>Rubric v{rubric.version} · how verdicts are banded</summary>
              <p>Yes/no answers: <b>pass</b> at or above {rubric.thresholds.noul_pass}, <b>fail</b> at or below {rubric.thresholds.noul_fail}, <b>review</b> between. A choice is acted on when its confidence is at least {rubric.thresholds.choice_confidence_act}; below that it is marked uncertain. Yes/no and choice numbers are not comparable, so they are banded separately. These thresholds are our policy, not a measured accuracy.</p>
            </details>
          )}
        </aside>
        <main className="detail">
          {selected === SUBMIT_VIEW ? (
            <SubmitPanel busy={submitBusy} setBusy={setSubmitBusy} onGraded={(g) => { setGrades((s) => ({ ...s, [SUBMIT_VIEW]: g })); setSelected("__submitted__"); }} />
          ) : selected === "__submitted__" && grades[SUBMIT_VIEW] ? (
            <>
              <div className="back"><button className="ghost small" onClick={() => setSelected(SUBMIT_VIEW)}>Grade another report</button></div>
              <GradePanel g={grades[SUBMIT_VIEW]} rubric={rubric} />
            </>
          ) : sel ? <GradePanel g={sel} rubric={rubric} /> : selBusy ? (
            <div className="empty"><div className="empty-art pulse" aria-hidden="true">{[0.98, 0.6, 0.94, 0.85, 0.88, 0.2].map((v, i) => <span key={i} style={{ height: 8 + v * 40 }} />)}</div>
              <h2>Grading {selected}…</h2><p>One request, eight questions, answered in parallel.</p></div>
          ) : (
            <div className="empty">
              <div className="empty-art" aria-hidden="true">{[0.98, 0.6, 0.94, 0.85, 0.88, 0.2].map((v, i) => <span key={i} style={{ height: 8 + v * 40 }} />)}</div>
              <h2>Pick a report, press Grade.</h2>
              <p>The record is trimmed to the fields the rubric references, sent once, and every probability comes back in a few hundred milliseconds. Grades are stored, so opening one again costs nothing.</p>
            </div>
          )}
        </main>
      </div>
      <footer className="foot">Built on OBTO · model <code>{rubric?.model || "jev-latest"}</code> · $0.042 per million input tokens, output free · displayed data is redacted; the model receives the full record.</footer>
    </div>
  );
}

const mount = () => {
  const el = document.getElementById("root");
  if (el) createRoot(el).render(<App />);
};
if (document.readyState === "loading") { document.addEventListener("DOMContentLoaded", mount); } else { mount(); }